import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper, Slider, Rating
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Explore as ExploreIcon, ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon, AccessTime as TimeIcon,
  MyLocation as GpsIcon, Person as PersonIcon,
  AssignmentTurnedIn as CheckIcon, Star as StarIcon,
  EventNote as EventIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmVisit, VisitRole, VisitType, VisitStatus, CrmOrganisation, CrmContact, CrmOpportunity } from '../types/crm';

const emptyVisit = (): Partial<CrmVisit> => ({
  role: 'PRESCRIPTEUR',
  type: 'PROSPECTION',
  scheduledDateTime: new Date().toISOString().substring(0, 16),
  objective: '',
  checklist: 'Catalogue produits, Échantillons profilés, Fiches d\'essais techniques',
  status: 'PLANNED',
  durationMinutes: 0,
  evaluationScore: 8,
  personsMet: '',
  topics: '',
  commitments: '',
  nextSteps: '',
  report: ''
});

const getStatusColor = (status: VisitStatus): 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'PLANNED': return 'info';
    case 'IN_PROGRESS': return 'warning';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'error';
    case 'RESCHEDULED': return 'default';
    default: return 'default';
  }
};

export const VisitsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [visitsList, setVisitsList] = useState<CrmVisit[]>([]);
  const [organisations, setOrganisations] = useState<CrmOrganisation[]>([]);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmVisit>>(emptyVisit());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vData, oData, cData, pData] = await Promise.all([
        crmApi.getVisits(),
        crmApi.getOrganisations(),
        crmApi.getContacts(),
        crmApi.getOpportunities()
      ]);
      setVisitsList(vData);
      setOrganisations(oData);
      setContacts(cData);
      setOpportunities(pData);
    } catch (err) {
      console.error('Error loading CRM visits:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyVisit());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (visit: CrmVisit) => {
    setFormData({ ...visit });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleCheckinGps = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = parseFloat(position.coords.latitude.toFixed(6));
          const lon = parseFloat(position.coords.longitude.toFixed(6));
          const nowStr = new Date().toISOString().substring(0, 16);
          setFormData({
            ...formData,
            status: 'IN_PROGRESS',
            checkinTime: nowStr,
            checkinLat: lat,
            checkinLon: lon
          });
          blobticMessage.success(`Check-in enregistré ! Position : ${lat}, ${lon}`);
        },
        (error) => {
          // Coordonnées de repli pour Alger si GPS non autorisé
          const nowStr = new Date().toISOString().substring(0, 16);
          setFormData({
            ...formData,
            status: 'IN_PROGRESS',
            checkinTime: nowStr,
            checkinLat: 36.7525,
            checkinLon: 3.0420
          });
          blobticMessage.warning('GPS indisponible, position approximative enregistrée.');
        }
      );
    }
  };

  const handleCheckout = () => {
    const nowStr = new Date().toISOString().substring(0, 16);
    let duration = 60;
    if (formData.checkinTime) {
      const checkinDate = new Date(formData.checkinTime);
      const checkoutDate = new Date();
      const diffMin = Math.round((checkoutDate.getTime() - checkinDate.getTime()) / 60000);
      if (diffMin > 0) duration = diffMin;
    }
    setFormData({
      ...formData,
      status: 'COMPLETED',
      checkoutTime: nowStr,
      durationMinutes: duration
    });
    blobticMessage.success(`Visite clôturée ! Durée calculée : ${duration} minutes.`);
  };

  const handleDelete = async (visit: CrmVisit) => {
    const visitId = (visit.id || visit.CVS_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.visits.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette visite terrain ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteVisit(visitId);
      if (res.success) {
        blobticMessage.success(t('crm.visits.deleteSuccess', 'Visite supprimée avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.objective?.trim()) {
      return t('crm.visits.objectiveRequired', 'L\'objectif de la visite est obligatoire.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CVS_IDF: formData.id || formData.CVS_IDF,
      CVS_CODE: formData.code || formData.CVS_CODE,
      CVS_ROLE: formData.role,
      CVS_TYPE: formData.type,
      COR_IDF: formData.organisationId || formData.COR_IDF,
      CCT_IDF: formData.contactId || formData.CCT_IDF,
      COP_IDF: formData.projectId || formData.COP_IDF,
      CVS_SCHEDULED_DATETIME: formData.scheduledDateTime,
      CVS_OBJECTIVE: formData.objective,
      CVS_CHECKLIST: formData.checklist,
      CVS_STATUS: formData.status,
      CVS_CHECKIN_TIME: formData.checkinTime,
      CVS_CHECKIN_LAT: formData.checkinLat,
      CVS_CHECKIN_LON: formData.checkinLon,
      CVS_CHECKOUT_TIME: formData.checkoutTime,
      CVS_DURATION_MINUTES: formData.durationMinutes,
      CVS_PERSONS_MET: formData.personsMet,
      CVS_TOPICS: formData.topics,
      CVS_COMMITMENTS: formData.commitments,
      CVS_NEXT_STEPS: formData.nextSteps,
      CVS_EVALUATION_SCORE: formData.evaluationScore,
      CVS_REPORT: formData.report,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.visits.editTitle', 'Fiche Visite Terrain') : t('crm.visits.newTitle', 'Planifier une Visite Terrain')}
        icon={<ExploreIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_visit', primaryKey: 'CVS_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Cadrage & Planification */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon color="primary" /> {t('crm.visits.secPlanning', 'Cadrage & Planification de la Visite')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.visits.role', 'Rôle')}
                      value={formData.role || 'PRESCRIPTEUR'}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as VisitRole })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PRESCRIPTEUR">{t('crm.visits.rolePrescripteur', 'Prescripteur / Prospecteur')}</MenuItem>
                      <MenuItem value="KAM">{t('crm.visits.roleKam', 'Key Account Manager (KAM)')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.visits.type', 'Type de Visite')}
                      value={formData.type || 'PROSPECTION'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as VisitType })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PROSPECTION">{t('crm.visits.typeProspection', 'Prospection Initiale')}</MenuItem>
                      <MenuItem value="PRECONISATION">{t('crm.visits.typePreconisation', 'Préconisation Technique')}</MenuItem>
                      <MenuItem value="PRESCRIPTION">{t('crm.visits.typePrescription', 'Prescription Formelle (CDC)')}</MenuItem>
                      <MenuItem value="SUIVI_CHANTIER">{t('crm.visits.typeSuiviChantier', 'Suivi Exécution Chantier')}</MenuItem>
                      <MenuItem value="REUNION_TECHNIQUE">{t('crm.visits.typeReunionTechnique', 'Réunion Technique / BET')}</MenuItem>
                      <MenuItem value="RECEPTION">{t('crm.visits.typeReception', 'Contrôle & Réception')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('common.status', 'Statut Visite')}
                      value={formData.status || 'PLANNED'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as VisitStatus })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="PLANNED">{t('crm.visits.statusPlanned', 'Planifiée')}</MenuItem>
                      <MenuItem value="IN_PROGRESS">{t('crm.visits.statusInProgress', 'Sur Site (En Cours)')}</MenuItem>
                      <MenuItem value="COMPLETED">{t('crm.visits.statusCompleted', 'Effectuée / Clôturée')}</MenuItem>
                      <MenuItem value="RESCHEDULED">{t('crm.visits.statusRescheduled', 'Reportée')}</MenuItem>
                      <MenuItem value="CANCELLED">{t('crm.visits.statusCancelled', 'Annulée')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.visits.datetime', 'Date & Heure Planifiée')}
                      type="datetime-local"
                      value={formData.scheduledDateTime || ''}
                      onChange={(e) => setFormData({ ...formData, scheduledDateTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.visits.organisation', 'Organisation Cible')}
                      value={formData.organisationId || ''}
                      onChange={(e) => setFormData({ ...formData, organisationId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('crm.visits.noOrg', '-- Aucune / Hors fiche --')}</MenuItem>
                      {organisations.map((org) => (
                        <MenuItem key={org.id || org.COR_IDF} value={org.id || org.COR_IDF}>
                          {org.name} ({org.type})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.visits.contact', 'Interlocuteur Clé')}
                      value={formData.contactId || ''}
                      onChange={(e) => setFormData({ ...formData, contactId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('common.none', '-- Aucun --')}</MenuItem>
                      {contacts.map((c) => (
                        <MenuItem key={c.id || c.CCT_IDF} value={c.id || c.CCT_IDF}>
                          {c.firstName} {c.lastName} ({c.company || c.role || 'Contact'})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.visits.project', 'Projet / Chantier Rattaché')}
                      value={formData.projectId || ''}
                      onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('crm.visits.noProject', '-- Aucun projet associé --')}</MenuItem>
                      {opportunities.map((opp) => (
                        <MenuItem key={opp.id || opp.COP_IDF} value={opp.id || opp.COP_IDF}>
                          {opp.title} ({opp.stage})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.visits.objective', 'Objectif Principal de la Visite')}
                      value={formData.objective || ''}
                      onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                      required
                      placeholder={t('crm.visits.objectivePlaceholder', 'Ex: Présentation de la gamme, remise des fiches AEV, négociation du métré...')}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.visits.checklist', 'Checklist & Éléments à emporter')}
                      value={formData.checklist || ''}
                      onChange={(e) => setFormData({ ...formData, checklist: e.target.value })}
                      placeholder={t('crm.visits.checklistPlaceholder', 'Échantillons d\'angles profilés, nuancier, documents techniques, convention de partenariat...')}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Exécution Terrain & Géolocalisation GPS */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GpsIcon color="primary" /> {t('crm.visits.secTerrain', 'Exécution Terrain & Pointage Géolocalisé')}
                </Typography>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<GpsIcon />}
                    onClick={handleCheckinGps}
                  >
                    {t('crm.visits.checkinBtn', 'Check-in Sur Site (GPS)')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={handleCheckout}
                    disabled={!formData.checkinTime}
                  >
                    {t('crm.visits.checkoutBtn', 'Clôturer Visite & Calculer Durée')}
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.visits.checkinTime', 'Heure d\'Arrivée (Check-in)')}
                      type="datetime-local"
                      value={formData.checkinTime || ''}
                      onChange={(e) => setFormData({ ...formData, checkinTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.visits.checkoutTime', 'Heure de Départ (Check-out)')}
                      type="datetime-local"
                      value={formData.checkoutTime || ''}
                      onChange={(e) => setFormData({ ...formData, checkoutTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('crm.visits.duration', 'Durée de l\'Entretien (Minutes)')}
                      type="number"
                      value={formData.durationMinutes ?? 0}
                      onChange={(e) => setFormData({ ...formData, durationMinutes: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.checkinLat', 'Latitude GPS')}
                      value={formData.checkinLat || ''}
                      onChange={(e) => setFormData({ ...formData, checkinLat: parseFloat(e.target.value) })}
                      placeholder="36.752500"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.checkinLon', 'Longitude GPS')}
                      value={formData.checkinLon || ''}
                      onChange={(e) => setFormData({ ...formData, checkinLon: parseFloat(e.target.value) })}
                      placeholder="3.042000"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 3: Déroulement & Compte-rendu */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="primary" /> {t('crm.visits.secReport', 'Compte-rendu & Évaluation de l\'Entretien')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label={t('crm.visits.personsMet', 'Personnes Rencontrées')}
                      value={formData.personsMet || ''}
                      onChange={(e) => setFormData({ ...formData, personsMet: e.target.value })}
                      placeholder={t('crm.visits.personsMetPlaceholder', 'Noms, titres, fonctions des interlocuteurs présents')}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('crm.visits.evalScore', 'Évaluation de l\'Entretien')} : {formData.evaluationScore || 8} / 10
                      </Typography>
                      <Slider
                        value={formData.evaluationScore || 8}
                        onChange={(_, val) => setFormData({ ...formData, evaluationScore: val as number })}
                        min={1}
                        max={10}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.topics', 'Sujets Abordés')}
                      value={formData.topics || ''}
                      onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.commitments', 'Engagements Pris (de part et d\'autre)')}
                      value={formData.commitments || ''}
                      onChange={(e) => setFormData({ ...formData, commitments: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.nextSteps', 'Prochaines Étapes / Relance')}
                      value={formData.nextSteps || ''}
                      onChange={(e) => setFormData({ ...formData, nextSteps: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.visits.report', 'Synthèse & Rapport Général')}
                      value={formData.report || ''}
                      onChange={(e) => setFormData({ ...formData, report: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.visits.title', 'Visites Terrain & Géolocalisation')}
      icon={<ExploreIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {visitsList.map((visit) => {
            const visitId = visit.id || visit.CVS_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={visitId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: visit.role === 'KAM' ? 'warning.main' : 'primary.main', width: 44, height: 44 }}>
                        <ExploreIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={visit.objective}>
                          {visit.objective}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {visit.code} • {t(visit.role, visit.role)}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(visit.status, visit.status)}
                        color={getStatusColor(visit.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TimeIcon fontSize="inherit" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {visit.scheduledDateTime ? new Date(visit.scheduledDateTime).toLocaleString() : '-'}
                        </Typography>
                      </Stack>

                      {visit.organisationName && (
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          🏢 {visit.organisationName}
                        </Typography>
                      )}

                      {visit.contactName && (
                        <Typography variant="body2" color="text.secondary">
                          👤 {visit.contactName}
                        </Typography>
                      )}

                      {visit.projectTitle && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          📁 {visit.projectTitle}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                        <Chip label={t(visit.type, visit.type)} size="small" variant="outlined" />
                        {visit.durationMinutes > 0 && (
                          <Chip label={`${visit.durationMinutes} min`} size="small" variant="outlined" />
                        )}
                        <Chip
                          icon={<StarIcon sx={{ fontSize: '14px !important', color: '#f59e0b' }} />}
                          label={`${visit.evaluationScore}/10`}
                          size="small"
                          sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#b45309' }}
                        />
                      </Stack>
                    </Stack>

                    {visit.report && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1
                        }}
                      >
                        {visit.report}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {visit.checkinLat ? `GPS: ${visit.checkinLat}, ${visit.checkinLon}` : t('crm.visits.noGps', 'Sans pointage GPS')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(visit)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(visit)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 110 },
            { id: 'objective', label: t('crm.visits.objective', 'Objectif Visite'), minWidth: 220 },
            { id: 'role', label: t('crm.visits.role', 'Rôle'), width: 130, chip: true },
            { id: 'type', label: t('crm.visits.type', 'Type'), width: 150, chip: true },
            { id: 'organisationName', label: t('crm.visits.organisation', 'Organisation'), width: 180 },
            { id: 'contactName', label: t('crm.visits.contact', 'Interlocuteur'), width: 160 },
            { id: 'scheduledDateTime', label: t('crm.visits.datetime', 'Date & Heure'), width: 160 },
            {
              id: 'status',
              label: t('common.status', 'Statut'),
              width: 130,
              chip: true,
              chipColorMap: {
                PLANNED: 'info',
                IN_PROGRESS: 'warning',
                COMPLETED: 'success',
                CANCELLED: 'error',
                RESCHEDULED: 'default'
              }
            },
            { id: 'durationMinutes', label: t('crm.visits.duration', 'Durée (m)'), width: 100 },
          ]}
          data={visitsList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.visits.search', 'Rechercher une visite, organisation, prescripteur...')}
          filterColumns={['code', 'objective', 'role', 'type', 'organisationName', 'contactName', 'status']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default VisitsPage;
